import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Badge,
  Accordion,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Axios } from "api/axios";
import { useHandleBack } from "helpers/functions/useHandleBack";
import { fetchPermissionsById } from "slices/permissions/thunk";
import { toast } from "react-toastify";

const UserDetails: React.FC = () => {
  document.title = "User Details";
  const { id } = useParams<{ id: string }>();
  const handleBack = useHandleBack("/user-list");
  const dispatch = useDispatch<any>();

  const [userData, setUserData] = useState<any>(null);
  const [permissions, setPermissions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!id) {
        setError("User ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Try to fetch user from list with search filter
        // First, try to get user by searching the list
        let user = null;
        let page = 1;
        const pageSize = 100; // Large page size to reduce API calls

        // Try searching through pages
        while (!user && page <= 5) {
          try {
            const response = await Axios.get(`api/user/list/`, {
              params: { page, pageSize },
            });

            const users = response.data?.data || [];
            user = users.find((u: any) => u.user_id === id || u.id === id);

            if (user) break;

            // If we got less results than pageSize, we've reached the end
            if (users.length < pageSize) break;

            page++;
          } catch (searchErr) {
            console.error("Search error:", searchErr);
            break;
          }
        }

        if (user) {
          setUserData(user);
        } else {
          setError("User not found");
          toast.error("User not found");
        }
      } catch (err: any) {
        console.error("Failed to fetch user details:", err);
        setError(
          err?.response?.data?.message || "Failed to fetch user details"
        );
        toast.error("Failed to fetch user details");
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      if (!id) return;

      try {
        setPermissionsLoading(true);
        const result = await dispatch(fetchPermissionsById(id)).unwrap();
        setPermissions(result);
      } catch (err: any) {
        console.error("Failed to fetch permissions:", err);
        // Don't show error toast for permissions, just log it
      } finally {
        setPermissionsLoading(false);
      }
    };

    if (id && userData) {
      fetchUserPermissions();
    }
  }, [id, userData, dispatch]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const renderPermissions = () => {
    if (permissionsLoading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading permissions...
        </div>
      );
    }

    if (!permissions || !permissions.apps_permissions) {
      return (
        <div className="text-center py-4 text-muted">
          No permissions assigned to this user.
        </div>
      );
    }

    return (
      <Accordion defaultActiveKey="0">
        {permissions.apps_permissions.map((app: any, appIndex: number) => (
          <Accordion.Item key={appIndex} eventKey={appIndex.toString()}>
            <Accordion.Header>
              <strong>{app.apps_name}</strong>
            </Accordion.Header>
            <Accordion.Body>
              {app.modules_permissions && app.modules_permissions.length > 0 ? (
                <div className="ms-3">
                  {app.modules_permissions.map(
                    (module: any, moduleIndex: number) => (
                      <div key={moduleIndex} className="mb-3">
                        <h6 className="text-primary mb-2">
                          {module.modules_name}
                        </h6>
                        {module.permissions && (
                          <div className="d-flex flex-wrap gap-2">
                            {Object.entries(module.permissions).map(
                              ([permissionName, allowed]: [string, any]) => (
                                <Badge
                                  key={permissionName}
                                  bg={allowed ? "success" : "danger"}
                                  className="px-3 py-2"
                                >
                                  {permissionName}
                                </Badge>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="text-muted">No modules assigned</div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  return (
    <div className="page-content px-4">
      <Card className="mb-4 p-3">
        {/* Breadcrumb / Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
          {/* Left: Back icon and heading */}
          <div className="d-flex align-items-center gap-2">
            <span
              style={{ fontSize: "24px", cursor: "pointer" }}
              onClick={handleBack}
            >
              ←
            </span>
            <h5 className="mb-0 fw-bold">View User Details</h5>
          </div>

          {/* Right: Breadcrumb */}
          <div className="text-muted">
            <Link
              to="/user-list"
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
            >
              User
            </Link>{" "}
            &gt;{" "}
            <span
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
            >
              User Details
            </span>
          </div>
        </div>

        {loading ? (
          <Container className="py-4 flex text-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Container>
        ) : error ? (
          <Container className="py-4 flex text-center">
            <h4>{error}</h4>
          </Container>
        ) : userData ? (
          <Card.Body>
            <h5 className="mb-0 fw-bold">User Details</h5>
            <hr />
            {/* Basic Information */}
            <Row className="mb-4">
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>First Name :</strong> {userData.first_name || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Last Name :</strong> {userData.last_name || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Username :</strong> {userData.username || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Email :</strong> {userData.email || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Mobile No :</strong> {userData.mobile_no || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>User Type :</strong>{" "}
                {userData.user_type__user_type || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Status :</strong>{" "}
                <Badge bg={userData.status ? "success" : "danger"}>
                  {userData.status ? "Active" : "Inactive"}
                </Badge>
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Designation :</strong> {userData.designation || "-"}
              </Col>
              {/* <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Organization :</strong>{" "}
                {userData.organization__organization_name || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Department :</strong>{" "}
                {userData.department__department_name || "-"}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Team :</strong> {userData.team__team_name || "-"}
              </Col> */}
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Created By :</strong>{" "}
                {userData.created_by_name || "N/A"} -{" "}
                {formatDate(userData.created_at)}
              </Col>
              <Col xl={4} md={6} xs={12} className="mb-3">
                <strong>Updated By :</strong>{" "}
                {userData.updated_by_name || "N/A"} -{" "}
                {formatDate(userData.updated_at)}
              </Col>
            </Row>

            {/* Address Information */}
            {(userData.address ||
              userData.city ||
              userData.state ||
              userData.country ||
              userData.pincode) && (
              <>
                <h5 className="mb-0 fw-bold mt-4">Address Information</h5>
                <hr />
                <Row>
                  <Col xl={4} md={6} xs={12} className="mb-3">
                    <strong>Address :</strong> {userData.address || "-"}
                  </Col>
                  <Col xl={4} md={6} xs={12} className="mb-3">
                    <strong>City :</strong> {userData.city || "-"}
                  </Col>
                  <Col xl={4} md={6} xs={12} className="mb-3">
                    <strong>State :</strong> {userData.state || "-"}
                  </Col>
                  <Col xl={4} md={6} xs={12} className="mb-3">
                    <strong>Country :</strong> {userData.country || "-"}
                  </Col>
                  <Col xl={4} md={6} xs={12} className="mb-3">
                    <strong>Pincode :</strong> {userData.pincode || "-"}
                  </Col>
                </Row>
              </>
            )}

            <hr />

            {/* Permissions */}
            <h5 className="mb-0 fw-bold mt-4">Permissions</h5>
            <p className="text-muted small my-2">
              <Badge bg="success" className="me-1">
                Green
              </Badge>{" "}
              = Has Permission{" "}
            </p>
            <p className="text-muted small my-2">
              <Badge bg="danger" className="me-1">
                Red
              </Badge>{" "}
              = No Permission
            </p>
            {/* <hr /> */}
            {renderPermissions()}
          </Card.Body>
        ) : null}
      </Card>

      {!loading && userData && (
        <Card>
          <div className="my-4 text-center">
            <Button onClick={handleBack} className="btn btn-secondary">
              Back
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UserDetails;
